import { wagmiConfig } from '@shared/configs/wagmi-config';
import { CHAIN_TOKEN_ADDRESS_MAP, ChainID } from '@shared/constants/chains';
import { getTokenAddressByChainId } from '@shared/utils/get-token-address-by-chain-id';
import { erc20Abi, maxInt256 } from 'viem';
import { readContract, waitForTransactionReceipt, writeContract } from 'wagmi/actions';

interface PowerPaymentDetails {
  contractAddress: string
  order: {
    orderId: string
    validUntil: number
    price: string
  }
}

const readErc20AbiContract = async (
  account: `0x${string}` | undefined,
  data: PowerPaymentDetails,
  chainId: ChainID,
) => {
  try {
    return await readContract(wagmiConfig, {
      abi: erc20Abi,
      address: CHAIN_TOKEN_ADDRESS_MAP[chainId],
      functionName: 'allowance',
      args: [account as `0x${string}`, data?.contractAddress as `0x${string}`],
    });
  } catch (error) {
    throw new Error(error as string);
  }
};

const waitForTranReceipt = async (txHash: `0x${string}`) => {
  const transactionReceipt = await waitForTransactionReceipt(wagmiConfig, {
    hash: txHash,
  });
  if (transactionReceipt.status !== 'success') {
    throw new Error('Transaction failed');
  }

  return transactionReceipt;
};

export const getErc20AbiApproval = async (
  data: PowerPaymentDetails,
  account: `0x${string}` | undefined,
  chainId: ChainID,
) => {
  try {
    const tokenAddress = getTokenAddressByChainId(chainId);
    const approvedAmount = await readErc20AbiContract(account, data, chainId);

    if (approvedAmount >= BigInt(data?.order?.price)) {
      return true;
    }

    const txHash = await writeContract(wagmiConfig, {
      abi: erc20Abi,
      address: tokenAddress,
      functionName: 'approve',
      args: [data?.contractAddress as `0x${string}`, maxInt256],
    });
    await waitForTranReceipt(txHash);

    return (
      (await readErc20AbiContract(account, data, chainId)) >= BigInt(data?.order?.price)
    );
  } catch (error) {
    throw new Error(error as string);
  }
};
